import React, { createContext, useContext } from 'react';
import { useAuth } from './AuthContext';
import { IndustryConfig, IndustryType } from '../types';

const defaultConfigs: Record<IndustryType, IndustryConfig> = {
  casing_running: {
    industryType: 'casing_running',
    displayName: 'Casing Running',
    description: 'Oil well casing installation and services',
    icon: 'Pipe',
    uiConfig: {
      jobForm: {
        showCasingSpecs: true,
        showRentalDates: false,
        showDeliveryLocation: false,
        showReturnLocation: false,
        showRoutePlanning: false,
        primarySections: ['casing_specs', 'services', 'crew'],
      },
      equipmentCategories: ['Power Tongs', 'Elevators', 'Spiders', 'Drift Tools'],
      serviceTypes: ['Surface Casing', 'Intermediate', 'Production', 'Liner'],
    },
  },
  oilfield_rental: {
    industryType: 'oilfield_rental',
    displayName: 'Oilfield Rental',
    description: 'Equipment rental and logistics',
    icon: 'Truck',
    uiConfig: {
      jobForm: {
        showCasingSpecs: false,
        showRentalDates: true,
        showDeliveryLocation: true,
        showReturnLocation: true,
        showRoutePlanning: false,
        primarySections: ['rental_dates', 'locations', 'equipment'],
      },
      equipmentCategories: ['Generators', 'Compressors', 'Light Towers', 'Trailers'],
      serviceTypes: ['Daily Rental', 'Weekly Rental', 'Monthly Rental'],
    },
  },
  well_servicing: {
    industryType: 'well_servicing',
    displayName: 'Well Servicing',
    description: 'Service rig operations',
    icon: 'Wrench',
    uiConfig: {
      jobForm: {
        showCasingSpecs: false,
        showRentalDates: false,
        showDeliveryLocation: false,
        showReturnLocation: false,
        showRoutePlanning: false,
        primarySections: ['well_info', 'services', 'crew'],
      },
      equipmentCategories: ['Service Rigs', 'Pump Trucks', 'Hot Oilers'],
      serviceTypes: ['Workover', 'Completion', 'Maintenance'],
    },
  },
  oilfield_trucking: {
    industryType: 'oilfield_trucking',
    displayName: 'Trucking',
    description: 'Transportation and logistics',
    icon: 'Truck',
    uiConfig: {
      jobForm: {
        showCasingSpecs: false,
        showRentalDates: false,
        showDeliveryLocation: false,
        showReturnLocation: false,
        showRoutePlanning: true,
        primarySections: ['route', 'load_details', 'equipment'],
      },
      equipmentCategories: ['Winch Trucks', 'Bed Trucks', 'Picker Trucks'],
      serviceTypes: ['Hot Shot', 'Heavy Haul', 'Rig Move'],
    },
  },
};

interface IndustryContextType {
  config: IndustryConfig;
  industryType: IndustryType;
  isCasingRunning: boolean;
  isOilfieldRental: boolean;
  isWellServicing: boolean;
  isOilfieldTrucking: boolean;
}

const IndustryContext = createContext<IndustryContextType | undefined>(undefined);

export function IndustryProvider({ children }: { children: React.ReactNode }) {
  const { company } = useAuth();

  // Picked from the tenant's enabled verticals ONLY. company.industryType is no
  // longer sent (api#890 removed it from login and /auth/me), so it is not a
  // fallback. The rentals key is `surface_rentals`; the `rental` this used to
  // test is a key no tenant stores, so oilfield_rental was unreachable.
  // A tenant with none of these keys (casing, downhole, midstream, pressure
  // testing) gets the casing config.
  const verticals: string[] = company?.enabledVerticals ?? company?.enabled_verticals ?? [];
  let industryType: IndustryType;
  if (verticals.includes('trucking')) {
    industryType = 'oilfield_trucking';
  } else if (verticals.includes('surface_rentals')) {
    industryType = 'oilfield_rental';
  } else if (verticals.includes('well_servicing')) {
    industryType = 'well_servicing';
  } else {
    industryType = 'casing_running';
  }
  const config = defaultConfigs[industryType];

  return (
    <IndustryContext.Provider value={{
      config,
      industryType,
      isCasingRunning: industryType === 'casing_running',
      isOilfieldRental: industryType === 'oilfield_rental',
      isWellServicing: industryType === 'well_servicing',
      isOilfieldTrucking: industryType === 'oilfield_trucking',
    }}>
      {children}
    </IndustryContext.Provider>
  );
}

export const useIndustry = () => {
  const context = useContext(IndustryContext);
  if (!context) throw new Error('useIndustry must be used within IndustryProvider');
  return context;
};